/**
 * Generic Campaigns & Coupons UI.
 *
 * The flow: pick a campaign from the left rail → see its stats, coupon list
 * and admin actions on the right. Issue Coupon, Run Lucky Draw, Queue
 * WhatsApp, and Export CSV are the four primary actions, each gated on the
 * campaign's runtime state (upcoming/active/ended/archived).
 *
 * The form for issuing a coupon is dynamic — it reads `campaign.fields[]`
 * from the API and renders the right input type per field. No code changes
 * needed to support a new campaign with different custom fields.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import Drawer, { FormField } from "@/components/Drawer";
import BranchSelect from "@/components/BranchSelect";
import { useAuth } from "@/context/AuthContext";
import {
  Megaphone,
  Plus,
  Trophy,
  WhatsappLogo,
  DownloadSimple,
  Archive,
  Trash,
} from "@phosphor-icons/react";

const STATUS_TONE = {
  upcoming: "text-amber-700 bg-amber-50",
  active: "text-emerald-700 bg-emerald-50",
  ended: "text-rose-700 bg-rose-50",
  archived: "text-ink-subtle bg-canvas-line/50",
};

function StatusBadge({ status }) {
  return (
    <span
      data-testid={`campaign-status-${status}`}
      className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${STATUS_TONE[status] || "bg-canvas-line"}`}
    >
      {status}
    </span>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className={`border border-canvas-line p-3 ${accent === "win" ? "bg-amber-50" : ""}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
        {label}
      </div>
      <div className="font-mono tabular-nums text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}

export default function CampaignsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const perms = user?.permissions || [];
  const canView = isAdmin || perms.includes("campaigns.view");
  const canCreate = isAdmin || perms.includes("campaigns.create");
  const canManage = isAdmin || perms.includes("campaigns.manage");
  const canDelete = isAdmin || perms.includes("campaigns.delete");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [winners, setWinners] = useState([]);
  const [search, setSearch] = useState("");
  const [showNewCoupon, setShowNewCoupon] = useState(false);
  const [showDraw, setShowDraw] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [qrCoupon, setQrCoupon] = useState(null); // opens CouponQrModal

  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get(canView ? "/campaigns" : "/campaigns/issuable");
      setCampaigns(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [selectedId, canView]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) { setSelected(null); setStats(null); setCoupons([]); setWinners([]); return; }
    try {
      if (!canView) {
        const camp = campaigns.find((c) => c.id === selectedId) || null;
        const { data } = await api.get(`/campaigns/${selectedId}/coupons/mine`, {
          params: search ? { search } : {},
        });
        setSelected(camp);
        setStats(null);
        setCoupons(data);
        setWinners([]);
        return;
      }
      const [campRes, statsRes, couponsRes, winnersRes] = await Promise.all([
        api.get(`/campaigns/${selectedId}`),
        api.get(`/campaigns/${selectedId}/stats`),
        api.get(`/campaigns/${selectedId}/coupons`, { params: search ? { search } : {} }),
        api.get(`/campaigns/${selectedId}/winners`),
      ]);
      setSelected(campRes.data);
      setStats(statsRes.data);
      setCoupons(couponsRes.data);
      setWinners(winnersRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [selectedId, search, canView, campaigns]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const queueWhatsApp = async () => {
    if (!window.confirm("Mark all pending coupons as queued for WhatsApp dispatch?")) return;
    try {
      const { data } = await api.post(`/campaigns/${selectedId}/whatsapp/queue-pending`);
      toast.success(`${data.queued} coupon(s) queued. Dispatch will run on the next batch.`);
      loadDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this campaign? Coupons remain but no new ones can be issued.")) return;
    try {
      await api.delete(`/campaigns/${selectedId}`);
      toast.success("Archived");
      loadCampaigns();
      loadDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    if (!coupons.length) { toast.error("No coupons to export"); return; }
    const headers = ["coupon_no", "name", "phone", "vehicle_vin", "sale_invoice_no", "model_variant", "branch_id", "issued_at", "is_winner", "won_position", "whatsapp_status"];
    const csv = [
      headers.join(","),
      ...coupons.map((c) => headers.map((h) => `"${(c[h] ?? "").toString().replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selected.coupon_prefix}-coupons-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rt = selected?.runtime_status;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="10 / Campaigns"
        title="Campaigns & Coupons"
        subtitle="Promotional drives, festival offers, walk-in lucky draws — issue coupons and pick winners."
        actions={canManage ? (
          <button
            onClick={() => setShowNewCampaign(true)}
            data-testid="new-campaign-btn"
            className="bg-ink text-white px-3 py-2.5 text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1.5"
          >
            <Plus size={12} weight="bold" /> New Campaign
          </button>
        ) : null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Left rail — campaigns list */}
        <div className="border border-canvas-line">
          <div className="px-3 py-2 border-b border-canvas-line font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            Campaigns ({campaigns.length})
          </div>
          {campaigns.length === 0 ? (
            <div className="p-4 text-xs text-ink-muted">No campaigns yet.</div>
          ) : (
            <ul>
              {campaigns.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelectedId(c.id)}
                    data-testid={`campaign-${c.coupon_prefix}`}
                    className={`w-full text-left px-3 py-2.5 border-b border-canvas-line/60 hover:bg-canvas-panel ${
                      selectedId === c.id ? "bg-canvas-panel" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm truncate">{c.name}</span>
                      <StatusBadge status={c.runtime_status} />
                    </div>
                    <div className="font-mono text-[10px] text-ink-muted mt-0.5">
                      {c.coupon_prefix} · {c.start_date} → {c.end_date}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right column — selected campaign detail */}
        <div className="space-y-4">
          {!selected ? (
            <div className="border border-canvas-line p-10 text-center text-ink-muted text-sm">
              <Megaphone size={28} className="mx-auto mb-2 opacity-50" />
              Pick a campaign on the left, or create one.
            </div>
          ) : (
            <>
              <div className="border border-canvas-line p-4 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
                      {selected.coupon_prefix} · {selected.start_date} → {selected.end_date}
                    </div>
                    <h2 className="font-display text-2xl font-bold mt-1">{selected.name}</h2>
                    {selected.description ? (
                      <p className="text-sm text-ink-muted mt-1 max-w-2xl">{selected.description}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {canCreate ? (
                      <button
                        onClick={() => setShowNewCoupon(true)}
                        disabled={(rt === "upcoming" && !canManage) || rt === "archived"}
                        title={rt === "upcoming" && !canManage ? `Campaign starts ${selected?.start_date}` : ""}
                        data-testid="issue-coupon-btn"
                        className="bg-ink text-white px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-brand disabled:opacity-40 flex items-center gap-1.5"
                      >
                        <Plus size={12} weight="bold" /> Issue Coupon
                        {rt === "upcoming" && canManage ? <span className="ml-1 text-amber-200">·early</span> : null}
                      </button>
                    ) : null}
                    {selected.eligible_for_draw && canManage ? (
                      <button
                        onClick={() => setShowDraw(true)}
                        disabled={rt !== "ended"}
                        data-testid="run-draw-btn"
                        title={rt !== "ended" ? "Lucky draw can run only after the campaign ends" : "Pick random winners"}
                        className="border border-amber-500 text-amber-700 px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-amber-50 disabled:opacity-40 flex items-center gap-1.5"
                      >
                        <Trophy size={12} weight="bold" /> Run Draw
                      </button>
                    ) : null}
                    {canManage ? (
                      <button
                        onClick={queueWhatsApp}
                        disabled={!stats?.whatsapp?.pending}
                        data-testid="queue-whatsapp-btn"
                        title={stats?.whatsapp?.pending ? "" : "No pending coupons"}
                        className="border border-canvas-line px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-canvas-panel disabled:opacity-40 flex items-center gap-1.5"
                      >
                        <WhatsappLogo size={12} weight="bold" />
                        Queue WhatsApp ({stats?.whatsapp?.pending || 0})
                      </button>
                    ) : null}
                    {canView ? (
                      <button
                        onClick={exportCsv}
                        data-testid="export-csv-btn"
                        className="border border-canvas-line px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-canvas-panel flex items-center gap-1.5"
                      >
                        <DownloadSimple size={12} weight="bold" /> CSV
                      </button>
                    ) : null}
                    {canManage && selected.status !== "archived" ? (
                      <button
                        onClick={archive}
                        data-testid="archive-btn"
                        title="Archive campaign"
                        className="text-rose-700 px-2 py-2 hover:bg-rose-50"
                      >
                        <Archive size={14} />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Stats strip */}
              {canView && stats ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <StatTile label="Total Coupons" value={stats.total_coupons} />
                  <StatTile label="Issued Today" value={stats.issued_today} />
                  <StatTile label="Winners" value={stats.winners} accent="win" />
                  <StatTile
                    label="WhatsApp Sent"
                    value={`${stats.whatsapp?.sent || 0} / ${stats.total_coupons}`}
                  />
                </div>
              ) : null}

              {/* Gift pool progress — shown only when campaign has a
                  fixed-budget gift pool defined. Lets ops see at a
                  glance how many coupons remain at each denomination
                  and how much of the ₹-budget is still un-issued. */}
              {canManage && selected.gift_pool && selected.gift_pool.length > 0 ? (
                (() => {
                  const totals = selected.gift_pool.reduce(
                    (a, r) => ({
                      total: a.total + (r.total || 0),
                      issued: a.issued + (r.issued || 0),
                      budget: a.budget + (r.total || 0) * (r.value || 0),
                      spent: a.spent + (r.issued || 0) * (r.value || 0),
                    }),
                    { total: 0, issued: 0, budget: 0, spent: 0 },
                  );
                  return (
                    <div className="border border-canvas-line bg-canvas-panel/40 p-3" data-testid="campaign-gift-pool-progress">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
                          Gift Pool · {totals.issued} / {totals.total} coupons issued · ₹{totals.spent.toLocaleString("en-IN")} of ₹{totals.budget.toLocaleString("en-IN")} spent
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {selected.gift_pool.map((row, i) => {
                          const remaining = (row.total || 0) - (row.issued || 0);
                          const pct = row.total ? Math.round(((row.issued || 0) / row.total) * 100) : 0;
                          return (
                            <div key={i} className="border border-canvas-line bg-canvas p-2">
                              <div className="flex items-center justify-between">
                                <div className="font-mono font-bold text-sm">₹{Number(row.value).toLocaleString("en-IN")}</div>
                                <div className={`font-mono text-[10px] ${remaining ? "text-ink-muted" : "text-rose-700"}`}>
                                  {remaining} left
                                </div>
                              </div>
                              <div className="mt-1 h-1.5 bg-canvas-line">
                                <div className="h-full bg-emerald-600" style={{ width: `${pct}%` }} />
                              </div>
                              <div className="mt-1 font-mono text-[10px] text-ink-muted">
                                {row.issued || 0} / {row.total || 0} issued
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()
              ) : null}

              {/* Coupons list */}
              <div className="border border-canvas-line">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-canvas-line">
                  <input
                    placeholder="Search by name, phone, coupon no…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="coupon-search"
                    className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                  />
                  <span className="font-mono text-[10px] text-ink-muted whitespace-nowrap">
                    {coupons.length} in view
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-canvas-panel border-b border-canvas-line">
                      <tr>
                        {(canView
                          ? ["Coupon #", "Name", "Phone", "Reference", "Gift", "Status", "Issued", "Winner"]
                          : ["Coupon #", "Name", "Phone", "Reference", "Gift", "Status", "Issued"]
                        ).map((h) => (
                          <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
                        ))}
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {coupons.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-10 text-center text-ink-muted text-xs">
                            No coupons issued yet for this campaign.
                          </td>
                        </tr>
                      ) : (
                        coupons.map((c) => (
                          <tr key={c.id} className="border-b border-canvas-line/50" data-testid={`coupon-row-${c.coupon_no}`}>
                            <td className="px-3 py-2 font-mono font-semibold">{c.coupon_no}</td>
                            <td className="px-3 py-2">{c.name}</td>
                            <td className="px-3 py-2 font-mono">{c.phone}</td>
                            <td className="px-3 py-2 font-mono text-[11px] text-ink-muted">
                              {[c.vehicle_vin, c.sale_invoice_no, c.model_variant].filter(Boolean).join(" · ") || "—"}
                            </td>
                            <td className="px-3 py-2 font-mono">
                              {c.gift_value ? (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-800">
                                  ₹{Number(c.gift_value).toLocaleString("en-IN")}
                                </span>
                              ) : (
                                <span className="text-ink-subtle">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {c.redeemed_at ? (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono font-semibold bg-neutral-800 text-white uppercase">
                                  Redeemed
                                </span>
                              ) : c.gift_value ? (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono font-semibold bg-amber-100 text-amber-800 uppercase">
                                  Pending
                                </span>
                              ) : (
                                <span className="text-ink-subtle text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-[11px] text-ink-muted">{(c.issued_at || "").slice(0, 16).replace("T", " ")}</td>
                            {canView ? (
                              <td className="px-3 py-2">
                                {c.is_winner ? (
                                  <span className="font-mono text-xs text-amber-700">
                                    #{c.won_position}
                                  </span>
                                ) : null}
                              </td>
                            ) : null}
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {c.gift_value && (
                                <button
                                  onClick={() => setQrCoupon(c)}
                                  data-testid={`coupon-qr-${c.coupon_no}`}
                                  className="mr-1 px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink"
                                >
                                  QR
                                </button>
                              )}
                              {canDelete && !c.is_winner && !c.redeemed_at ? (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete coupon ${c.coupon_no}?`)) return;
                                    try {
                                      await api.delete(`/campaigns/${selectedId}/coupons/${c.id}`);
                                      toast.success("Deleted"); loadDetail();
                                    } catch (e) { toast.error(formatApiError(e)); }
                                  }}
                                  className="text-rose-700 hover:bg-rose-50 p-1"
                                >
                                  <Trash size={12} />
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Winners panel */}
              {canView && winners.length > 0 ? (
                <div className="border border-amber-200 bg-amber-50/40">
                  <div className="px-3 py-2 border-b border-amber-200 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-700">
                    🏆 Winners — drawn {(winners[0]?.won_at || "").slice(0, 10)} by {winners[0]?.won_by_name}
                  </div>
                  <ol className="divide-y divide-amber-200/50">
                    {winners.map((w) => (
                      <li key={w.id} className="px-3 py-2 flex items-center justify-between text-sm">
                        <span className="font-mono font-semibold">#{w.won_position} · {w.coupon_no}</span>
                        <span>{w.name} ({w.phone})</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {selected ? (
        <IssueCouponDrawer
          open={showNewCoupon}
          campaign={selected}
          onClose={() => setShowNewCoupon(false)}
          onSaved={() => { setShowNewCoupon(false); loadDetail(); }}
          onIssued={(coupon) => setQrCoupon(coupon)}
        />
      ) : null}
      {selected ? (
        <DrawDialog
          open={showDraw}
          campaign={selected}
          poolSize={stats?.total_coupons || 0}
          existingWinners={winners.length}
          onClose={() => setShowDraw(false)}
          onDrawn={() => { setShowDraw(false); loadDetail(); }}
        />
      ) : null}
      <CampaignFormDrawer
        open={showNewCampaign}
        onClose={() => setShowNewCampaign(false)}
        onSaved={() => { setShowNewCampaign(false); loadCampaigns(); }}
      />
      {qrCoupon && selected && (
        <CouponQrModal
          coupon={qrCoupon}
          campaign={selected}
          onClose={() => { setQrCoupon(null); loadDetail(); }}
        />
      )}
    </div>
  );
}


// -------------------- Coupon QR / Print modal ------------------------------

function CouponQrModal({ coupon, campaign, onClose }) {
  // The QR endpoint is protected — we fetch as a blob (auth header) and
  // stitch it into an object URL so the browser can render the PNG.
  const [qrUrl, setQrUrl] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    api.get(`/campaigns/${campaign.id}/coupons/${coupon.id}/qr`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setQrUrl(objectUrl);
      })
      .catch((e) => !cancelled && setErr(formatApiError(e)));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [campaign.id, coupon.id]);

  const printCoupon = () => {
    const w = window.open("", "_blank", "width=420,height=640");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
    const inrFmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
    w.document.write(`
      <!doctype html><html><head><title>${coupon.coupon_no}</title>
      <style>
        body { font-family: system-ui, sans-serif; padding: 24px; text-align: center; color: #111; }
        .box { border: 2px dashed #111; padding: 24px; }
        .prefix { font-family: ui-monospace, monospace; letter-spacing: 0.3em; font-size: 11px; color: #555; }
        .value { font-size: 48px; font-weight: 800; margin: 8px 0 16px; }
        .no { font-family: ui-monospace, monospace; font-weight: 700; margin-top: 12px; }
        .name { margin-top: 4px; font-size: 13px; color: #333; }
        img { width: 220px; height: 220px; }
        .terms { margin-top: 16px; font-size: 10px; color: #666; }
      </style></head><body>
      <div class="box">
        <div class="prefix">${(campaign.name || "GIFT COUPON").toUpperCase()}</div>
        <div class="value">${inrFmt(coupon.gift_value)}</div>
        <img src="${qrUrl}" alt="QR" />
        <div class="no">${coupon.coupon_no}</div>
        <div class="name">${coupon.name} · ${coupon.phone}</div>
        <div class="terms">Scan or present at counter · Valid until ${campaign.end_date} · One-time use</div>
      </div>
      <script>window.onload = () => setTimeout(() => window.print(), 200);</script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="coupon-qr-modal">
      <div className="bg-canvas border border-canvas-line max-w-md w-full">
        <div className="border-b border-canvas-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Gift Coupon</div>
            <div className="text-lg font-semibold font-mono">{coupon.coupon_no}</div>
          </div>
          <button onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <Trash size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 text-center">
          <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Value</div>
          <div className="text-4xl font-bold text-emerald-700 mt-1 mb-4" data-testid="coupon-qr-value">
            ₹{Number(coupon.gift_value || 0).toLocaleString("en-IN")}
          </div>
          <div className="border border-canvas-line inline-block p-3 bg-white">
            {err ? (
              <div className="text-destructive text-xs w-[220px] py-16">{err}</div>
            ) : qrUrl ? (
              <img src={qrUrl} alt="Coupon QR" className="w-56 h-56" data-testid="coupon-qr-image" />
            ) : (
              <div className="text-ink-muted text-xs w-56 h-56 flex items-center justify-center">Generating…</div>
            )}
          </div>
          <div className="mt-3 text-sm text-ink">
            <b>{coupon.name}</b> · <span className="font-mono">{coupon.phone}</span>
          </div>
          <div className="mt-1 font-mono text-[10px] text-ink-muted">
            Valid until {campaign.end_date} · One-time use
          </div>
          {coupon.redeemed_at && (
            <div className="mt-3 inline-block px-2 py-1 bg-neutral-800 text-white text-[11px] font-mono uppercase">
              Redeemed {(coupon.redeemed_at || "").slice(0, 16).replace("T", " ")}
            </div>
          )}
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex items-center justify-between gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-canvas-line hover:border-ink"
          >
            Close
          </button>
          <button
            onClick={printCoupon}
            disabled={!qrUrl}
            data-testid="coupon-qr-print"
            className="px-4 py-2 bg-ink text-white text-xs font-semibold uppercase tracking-wider hover:bg-brand disabled:opacity-40"
          >
            Print Coupon
          </button>
        </div>
      </div>
    </div>
  );
}


// ----------------- Issue Coupon Drawer ------------------------------------

function IssueCouponDrawer({ open, campaign, onClose, onSaved, onIssued }) {
  const [form, setForm] = useState({
    name: "", phone: "", branch_id: "", vehicle_vin: "", sale_invoice_no: "", model_variant: "", data: {},
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({
      name: "", phone: "", branch_id: "", vehicle_vin: "", sale_invoice_no: "", model_variant: "", data: {},
    });
  }, [open]);

  const setData = (k, v) => setForm((f) => ({ ...f, data: { ...f.data, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/coupons`, form);
      toast.success(`Coupon ${data.coupon_no} issued for ${data.name}`);
      // Gift coupons: pop the QR modal immediately so the salesperson
      // can hand-off / print / whatsapp the coupon to the customer.
      if (data.gift_value) onIssued?.(data);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={campaign?.coupon_prefix}
      title="Issue Coupon"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="coupon-form">
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            testId="coupon-name"
            required
          />
          <FormField
            label="Phone"
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            testId="coupon-phone"
            placeholder="10-digit number"
            mono
            required
          />
        </div>
        <BranchSelect
          value={form.branch_id}
          onChange={(v) => setForm({ ...form, branch_id: v })}
          testId="coupon-branch"
        />
        <div className="border border-dashed border-canvas-line p-3 space-y-3" data-testid="coupon-reference-fields">
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted">
            Optional customer / vehicle reference
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FormField
              label="VIN"
              value={form.vehicle_vin}
              onChange={(v) => setForm({ ...form, vehicle_vin: v.toUpperCase() })}
              testId="coupon-vehicle-vin"
              placeholder="Optional"
              mono
            />
            <FormField
              label="Sale / Invoice No"
              value={form.sale_invoice_no}
              onChange={(v) => setForm({ ...form, sale_invoice_no: v })}
              testId="coupon-sale-invoice-no"
              placeholder="Optional"
              mono
            />
            <FormField
              label="Model / Variant"
              value={form.model_variant}
              onChange={(v) => setForm({ ...form, model_variant: v })}
              testId="coupon-model-variant"
              placeholder="Optional"
            />
          </div>
        </div>
        {(campaign?.fields || []).map((fld) => (
          <DynamicField
            key={fld.key}
            field={fld}
            value={form.data[fld.key]}
            onChange={(v) => setData(fld.key, v)}
          />
        ))}
        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="coupon-save"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
          >
            {saving ? "Issuing…" : "Issue & Save"}
          </button>
        </div>
      </form>
    </Drawer>
  );
}


function DynamicField({ field, value, onChange }) {
  if (field.type === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`field-${field.key}`}
        />
        {field.label}
        {field.required ? <span className="text-rose-600">*</span> : null}
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">
          {field.label} {field.required ? <span className="text-rose-600">*</span> : null}
        </label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`field-${field.key}`}
          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        >
          <option value="">—</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  return (
    <FormField
      label={`${field.label}${field.required ? " *" : ""}`}
      type={field.type === "phone" || field.type === "email" ? "text" : (field.type === "number" ? "number" : "text")}
      value={value || ""}
      onChange={onChange}
      placeholder={field.placeholder}
      testId={`field-${field.key}`}
    />
  );
}


// ----------------- Draw dialog --------------------------------------------

function DrawDialog({ open, campaign, poolSize, existingWinners, onClose, onDrawn }) {
  const [count, setCount] = useState(3);
  const [reset, setReset] = useState(false);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => { if (open) { setCount(3); setReset(false); } }, [open]);

  const run = async () => {
    setDrawing(true);
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/draw`, {
        winners_count: Number(count) || 1,
        reset,
      });
      toast.success(`Drew ${data.winners_count} winner(s) from a pool of ${data.pool_size}.`);
      onDrawn && onDrawn();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} eyebrow="Lucky Draw" title={campaign?.name}>
      <div className="space-y-4">
        <div className="border border-amber-200 bg-amber-50/50 p-3 text-xs">
          The system uses a cryptographically-strong random shuffle to pick winners.
          Winner data (coupon, timestamp, admin who ran the draw) is stamped on the
          coupon doc for audit.
        </div>
        <FormField
          label="How many winners?"
          type="number"
          value={count}
          onChange={(v) => setCount(v)}
          testId="draw-count"
          mono
          required
        />
        <div className="text-xs text-ink-muted">
          Pool size: <span className="font-mono">{poolSize}</span>
          {existingWinners > 0 ? (
            <>{" · "}existing winners: <span className="font-mono">{existingWinners}</span></>
          ) : null}
        </div>
        {existingWinners > 0 ? (
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={reset}
              onChange={(e) => setReset(e.target.checked)}
              data-testid="draw-reset"
            />
            Reset existing winners and redraw
          </label>
        ) : null}
        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button onClick={onClose} className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink">
            Cancel
          </button>
          <button
            onClick={run}
            disabled={drawing}
            data-testid="draw-run"
            className="px-4 py-2.5 bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-60 flex items-center gap-1.5"
          >
            <Trophy size={14} weight="bold" />
            {drawing ? "Drawing…" : "Run Draw"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}


// ----------------- Campaign form drawer -----------------------------------

const EMPTY_FIELD = { key: "", label: "", type: "text", required: false, options: [], placeholder: "" };

function CampaignFormDrawer({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    coupon_prefix: "",
    start_date: "",
    end_date: "",
    dedup_rule: "one_per_phone",
    eligible_for_draw: true,
    terms: "",
    fields: [],
    gift_pool: [],
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({
      name: "", description: "", coupon_prefix: "",
      start_date: "", end_date: "",
      dedup_rule: "one_per_phone", eligible_for_draw: true,
      terms: "", fields: [], gift_pool: [],
    });
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setField = (i, k, v) =>
    setForm((f) => ({ ...f, fields: f.fields.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const addField = () => set("fields", [...form.fields, { ...EMPTY_FIELD }]);
  const rmField = (i) => set("fields", form.fields.filter((_, j) => j !== i));

  // Fixed-budget gift pool editor. Each row = one denomination + how
  // many coupons of that value the campaign is authorised to hand out.
  const addPoolRow = () => set("gift_pool", [...form.gift_pool, { value: 0, total: 0 }]);
  const rmPoolRow = (i) => set("gift_pool", form.gift_pool.filter((_, j) => j !== i));
  const setPoolRow = (i, k, v) =>
    set("gift_pool", form.gift_pool.map((row, j) => j === i ? { ...row, [k]: parseInt(v || 0, 10) || 0 } : row));

  const poolTotals = form.gift_pool.reduce(
    (a, r) => ({
      coupons: a.coupons + (r.total || 0),
      budget: a.budget + (r.total || 0) * (r.value || 0),
    }),
    { coupons: 0, budget: 0 },
  );

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/campaigns", {
        ...form,
        coupon_prefix: (form.coupon_prefix || "").toUpperCase().trim(),
      });
      toast.success("Campaign created");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} eyebrow="Campaigns" title="New Campaign">
      <form onSubmit={save} className="space-y-4" data-testid="campaign-form">
        <FormField label="Name" value={form.name} onChange={(v) => set("name", v)} testId="campaign-name" required />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Coupon Prefix (max 10, A-Z 0-9)" value={form.coupon_prefix} onChange={(v) => set("coupon_prefix", v)} testId="campaign-prefix" mono required />
          <FormField label="Dedup Rule" value={form.dedup_rule} onChange={(v) => set("dedup_rule", v)} type="text" testId="campaign-dedup" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Start date" type="date" value={form.start_date} onChange={(v) => set("start_date", v)} testId="campaign-start" required />
          <FormField label="End date" type="date" value={form.end_date} onChange={(v) => set("end_date", v)} testId="campaign-end" required />
        </div>
        <FormField label="Description" value={form.description} onChange={(v) => set("description", v)} testId="campaign-desc" />
        <FormField label="Terms (printed on coupon footer)" value={form.terms} onChange={(v) => set("terms", v)} testId="campaign-terms" />
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.eligible_for_draw}
            onChange={(e) => set("eligible_for_draw", e.target.checked)}
            data-testid="campaign-eligible"
          />
          Eligible for lucky draw at end
        </label>

        <div className="border-t border-canvas-line pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
                Fixed-Budget Gift Pool
              </div>
              <div className="text-[10px] text-ink-muted">
                Set exact coupon counts per denomination — each new coupon draws randomly from what's remaining. Leave blank for a non-gift campaign.
              </div>
            </div>
            <button
              type="button"
              onClick={addPoolRow}
              data-testid="campaign-pool-add"
              className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink"
            >
              + Row
            </button>
          </div>
          {form.gift_pool.length === 0 ? (
            <div className="border border-dashed border-canvas-line px-3 py-4 text-center text-[11px] text-ink-muted">
              No pool defined. Click <b>+ Row</b> to add e.g. 500 coupons × ₹500 each.
            </div>
          ) : (
            <table className="w-full text-sm border border-canvas-line">
              <thead className="bg-canvas-panel border-b border-canvas-line">
                <tr>
                  <th className="px-2 py-1 font-mono text-[10px] uppercase text-left">Value (₹)</th>
                  <th className="px-2 py-1 font-mono text-[10px] uppercase text-left">Count</th>
                  <th className="px-2 py-1 font-mono text-[10px] uppercase text-right">Subtotal</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.gift_pool.map((row, i) => (
                  <tr key={i} className="border-b border-canvas-line/50" data-testid={`campaign-pool-row-${i}`}>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        value={row.value || ""}
                        onChange={(e) => setPoolRow(i, "value", e.target.value)}
                        data-testid={`campaign-pool-value-${i}`}
                        className="w-full px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm"
                        min={1}
                        placeholder="500"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        value={row.total || ""}
                        onChange={(e) => setPoolRow(i, "total", e.target.value)}
                        data-testid={`campaign-pool-total-${i}`}
                        className="w-full px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm"
                        min={1}
                        placeholder="500"
                      />
                    </td>
                    <td className="px-2 py-1 font-mono text-right text-sm">
                      ₹{((row.value || 0) * (row.total || 0)).toLocaleString("en-IN")}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <button type="button" onClick={() => rmPoolRow(i)} className="text-rose-700 hover:bg-rose-50 p-1">
                        <Trash size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-canvas-panel border-t border-canvas-line">
                <tr>
                  <td className="px-2 py-2 font-mono text-[10px] uppercase text-ink-muted">Totals</td>
                  <td className="px-2 py-2 font-mono text-sm font-semibold" data-testid="campaign-pool-total-coupons">
                    {poolTotals.coupons.toLocaleString("en-IN")} coupons
                  </td>
                  <td className="px-2 py-2 font-mono text-sm font-semibold text-right" data-testid="campaign-pool-total-budget">
                    ₹{poolTotals.budget.toLocaleString("en-IN")}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <div className="border-t border-canvas-line pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
              Custom Fields ({form.fields.length})
            </span>
            <button type="button" onClick={addField} data-testid="campaign-add-field" className="text-xs underline hover:text-ink">
              + Add field
            </button>
          </div>
          {form.fields.map((fld, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_100px_70px_auto] gap-2 mb-2 items-center">
              <input
                placeholder="key (e.g. vehicle_interest)"
                value={fld.key}
                onChange={(e) => setField(i, "key", e.target.value)}
                className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono"
              />
              <input
                placeholder="Label"
                value={fld.label}
                onChange={(e) => setField(i, "label", e.target.value)}
                className="px-2 py-1 bg-canvas border border-canvas-line text-xs"
              />
              <select
                value={fld.type}
                onChange={(e) => setField(i, "type", e.target.value)}
                className="px-2 py-1 bg-canvas border border-canvas-line text-xs"
              >
                <option value="text">text</option>
                <option value="email">email</option>
                <option value="phone">phone</option>
                <option value="number">number</option>
                <option value="select">select</option>
                <option value="checkbox">checkbox</option>
              </select>
              <label className="inline-flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={fld.required}
                  onChange={(e) => setField(i, "required", e.target.checked)}
                />
                req
              </label>
              <button type="button" onClick={() => rmField(i)} className="text-rose-700 px-1 hover:bg-rose-50">
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>

        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="campaign-save"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
          >
            {saving ? "Saving…" : "Create Campaign"}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
